import { useEffect, useState } from 'react';
import { Icon } from '../../../layout/Icon.jsx';
import { formatIQD } from '../../../shared/format.js';
import { useAuth } from '../../../shared/useAuth.js';
import { useTopupCard, useTopupCashboxes } from '../hooks/useCards.js';
import { useShiftStatus, useShiftUI } from '../../../shared/useShift.js';
import { ShiftDrawerNote } from '../../../shared/ShiftDrawerNote.jsx';

export function TopupModal({ open, card, onClose }) {
  const [amount, setAmount] = useState(5000);
  const [cashboxId, setCashboxId] = useState('');
  const topup = useTopupCard();
  const cashboxesQuery = useTopupCashboxes(open);
  const cashboxes = cashboxesQuery.data || [];

  const me = useAuth((s) => s.user);
  const assignedCashboxId = me?.assignedCashboxId?._id || me?.assignedCashboxId || null;

  useEffect(() => { if (open) setAmount(5000); }, [open]);

  // تعبئة الكارت عملية نقدية: تتطلب شفتاً، والنقد يدخل درجه
  const { shift, blocked, cashboxId: shiftBox } = useShiftStatus();
  const openShiftModal = useShiftUI((s) => s.openModal);
  useEffect(() => {
    if (open && shiftBox) setCashboxId(shiftBox);
  }, [open, shiftBox]);

  useEffect(() => {
    if (!open || shiftBox || cashboxId || !cashboxes.length) return;
    const preferred = assignedCashboxId
      ? cashboxes.find((cb) => String(cb._id || cb.id) === String(assignedCashboxId))
      : null;
    const pick = preferred || cashboxes[0];
    setCashboxId(pick._id || pick.id);
  }, [open, cashboxes, assignedCashboxId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !card) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    const a = Math.round(Number(amount) || 0);
    if (a <= 0 || !cashboxId) return;
    await topup.mutateAsync(
      { id: card._id || card.id, amount: a, cashboxId },
      { onSuccess: () => onClose() }
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4"
    >
      <form onSubmit={handleSubmit} className="bg-white rounded-[14px] shadow-pop w-full max-w-[400px] overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <h3 className="font-extrabold text-[15px] m-0">شحن الكارت {card.uid}</h3>
          <button type="button" onClick={onClose} className="text-ink-3 hover:text-ink">
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-paper-2 rounded-[10px] p-3 text-[12.5px]">
            <div className="flex items-center justify-between">
              <span className="text-ink-3">الرصيد الحالي:</span>
              <span className="font-bold">{formatIQD(card.balance || 0)}</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-ink-3">بعد الشحن:</span>
              <span className="font-bold text-green-700">{formatIQD((card.balance || 0) + (Number(amount) || 0))}</span>
            </div>
          </div>
          <div>
            <label className="block text-[12.5px] font-semibold mb-1">مبلغ الشحن (د.ع)</label>
            <input
              type="number"
              className="input text-[14px]"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="1"
              autoFocus
            />
            <div className="flex gap-1.5 mt-2">
              {[5000, 10000, 25000, 50000].map((v) => (
                <button
                  key={v}
                  type="button"
                  className="btn btn-sm flex-1"
                  onClick={() => setAmount((a) => Math.max(0, Math.round(Number(a) || 0)) + v)}
                >
                  +{v.toLocaleString('en-US')}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[12.5px] font-semibold mb-1">
              القاصة المستلِمة <span className="text-red">*</span>
            </label>
            {blocked ? (
              <div className="rounded-[10px] px-3 py-2 text-[12.5px]" style={{ background: '#FFF7ED', color: '#9A3412' }}>
                لا يوجد شفت مفتوح — تعبئة الكارت تتطلب شفتاً.{' '}
                <button type="button" className="underline font-bold" onClick={() => { onClose(); openShiftModal('open'); }}>
                  فتح شفت
                </button>
              </div>
            ) : shift ? (
              <ShiftDrawerNote shift={shift} />
            ) : (
              <select
                className="input text-[13px]"
                value={cashboxId}
                onChange={(e) => setCashboxId(e.target.value)}
              >
                <option value="">— اختر القاصة —</option>
                {cashboxes.map((cb) => (
                  <option key={cb._id || cb.id} value={cb._id || cb.id}>
                    {cb.name}
                  </option>
                ))}
              </select>
            )}
            <div className="text-[11px] text-ink-3 mt-1 leading-relaxed">
              المبلغ يدخل هذه القاصة ويُسجَّل إيراداً باسم «تعبئة الكروت».
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-line flex justify-end gap-2 bg-paper-2">
          <button type="button" className="btn" onClick={onClose}>إلغاء</button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={topup.isPending || Number(amount) <= 0 || !cashboxId || blocked}
          >
            {topup.isPending ? 'جاري الشحن…' : 'شحن'}
          </button>
        </div>
      </form>
    </div>
  );
}
